import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { useVitalsTabIndicator } from './useVitalsTabIndicator';
import './VitalsTabs.css';

export const parsePatientVitals = (vitalsRaw) => {
  const tokens = [];
  const pushToken = (value) => {
    const token = String(value ?? '').trim().toUpperCase();
    if (!token) return;
    tokens.push(token);
  };

  if (Array.isArray(vitalsRaw)) {
    vitalsRaw.forEach(pushToken);
  } else if (vitalsRaw && typeof vitalsRaw === 'object') {
    Object.entries(vitalsRaw).forEach(([key, enabled]) => {
      if (enabled === true || enabled === 1 || enabled === '1' || enabled === 'true') {
        pushToken(key);
      }
    });
  } else {
    const raw = String(vitalsRaw ?? '')
      .toUpperCase()
      .replace(/[|/]/g, ',');
    raw.split(',').forEach(pushToken);
  }

  const normalized = new Set(
    tokens.flatMap((t) => t.replace(/[_-]+/g, ' ').split(/\s+/)).filter(Boolean)
  );
  const joined = tokens.join(' ').replace(/[_-]+/g, ' ');

  const hasBP =
    normalized.has('BP') ||
    joined.includes('BLOOD PRESSURE') ||
    normalized.has('BLOODPRESSURE') ||
    normalized.has('BLOOD_PRESSURE');
  const hasBG =
    normalized.has('BG') ||
    joined.includes('BLOOD GLUCOSE') ||
    normalized.has('BLOODGLUCOSE') ||
    normalized.has('BLOOD_GLUCOSE') ||
    normalized.has('GLUCOSE');
  const hasWT =
    normalized.has('WT') ||
    normalized.has('WEIGHT') ||
    joined.includes('WEIGHT');

  if (tokens.length === 0) {
    return { bp: true, bg: true, wt: true };
  }

  return { bp: hasBP, bg: hasBG, wt: hasWT };
};

const VitalsTabs = ({ practiceId, patientId, vitals, active, mode = 'measurement', className = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const available = useMemo(() => parsePatientVitals(vitals), [vitals]);

  const tabs = useMemo(() => {
    const base = `/practices/${practiceId}/patients/${patientId}/measurements`;
    return [
      {
        key: 'blood-pressure',
        label: mode === 'chart' ? 'Blood Pressure Chart' : 'Blood Pressure',
        path: mode === 'chart' ? `${base}/blood-pressure/chart` : `${base}/blood-pressure`,
        enabled: available.bp
      },
      {
        key: 'blood-glucose',
        label: mode === 'chart' ? 'Blood Glucose Chart' : 'Blood Glucose',
        path: mode === 'chart' ? `${base}/blood-glucose/chart` : `${base}/blood-glucose`,
        enabled: available.bg
      },
      {
        key: 'weight',
        label: mode === 'chart' ? 'Weight Graphs' : 'Weight',
        path: mode === 'chart' ? `${base}/weight/chart` : `${base}/weight`,
        enabled: available.wt
      }
    ];
  }, [available.bp, available.bg, available.wt, practiceId, patientId, mode]);

  const enabledCount = useMemo(() => tabs.filter((t) => t.enabled).length, [tabs]);
  const { tabsWrapRef, tabIndicatorStyle, indicatorReady } = useVitalsTabIndicator(active, enabledCount);

  const firstEnabledPath = useMemo(() => tabs.find((t) => t.enabled)?.path || null, [tabs]);

  useEffect(() => {
    const current = tabs.find((t) => t.key === active);
    if (!current || current.enabled || !firstEnabledPath) return;
    navigate(firstEnabledPath, { replace: true, state: location.state });
  }, [tabs, active, firstEnabledPath, navigate, location.state]);

  return (
    <div className={`res-vitals-tabs-wrap ${className}`.trim()} ref={tabsWrapRef}>
      <span
        className={`res-vitals-tab-indicator${indicatorReady ? ' is-ready' : ''}`}
        style={tabIndicatorStyle}
        aria-hidden="true"
      />
      <div className="res-vitals-tabs" role="tablist">
        {tabs.map((t) => {
          const isActive = t.enabled && active === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              className={`res-vitals-tab${isActive ? ' is-active' : ''}${!t.enabled ? ' is-disabled' : ''}`.trim()}
              disabled={!t.enabled}
              title={!t.enabled ? 'Not assigned for this patient' : undefined}
              aria-disabled={!t.enabled}
              aria-selected={isActive}
              onClick={() => {
                if (!t.enabled || isActive) return;
                navigate(t.path, { state: location.state });
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

VitalsTabs.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  vitals: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
  active: PropTypes.oneOf(['blood-pressure', 'blood-glucose', 'weight']).isRequired,
  mode: PropTypes.oneOf(['measurement', 'chart']),
  className: PropTypes.string
};

export default VitalsTabs;
